// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import DraftImage from '../DraftImage.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { EngageNoteCollapsibleInternal } from './EngageNoteCollapsibleInternal.js';
import { illustrationColor, productColor } from '../designSystem.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function EANMisconceptionsPanel(props: {|
  children: ChildrenArray<Element<typeof EANMisconceptionsDetail>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    EANMisconceptionsPanel,
  );
  if (failedValidation) return null;
  const children = React.Children.toArray(props.children);
  const cellStyle = {
    display: 'table-cell',
    verticalAlign: 'top',
    textAlign: 'left',
    color: productColor.shuttleGrey,
  };
  return (
    <EngageNoteCollapsibleInternal title="Misconceptions">
      {children.map((child, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'table', tableLayout: 'fixed' }}>
            <div style={cellStyle}>{child}</div>
            <div style={{ width: 20 + 4, ...cellStyle }} />
          </div>
          {i !== children.length - 1 && (
            <div>
              {/* this achieves a 32px gap with 2px line in the middle */}
              <div
                style={{
                  height: 16,
                  borderBottom: `1px solid ${productColor.ironDark}`,
                }}
              />
              <div
                style={{
                  height: 16,
                  borderTop: `1px solid ${productColor.ironDark}`,
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </EngageNoteCollapsibleInternal>
  );
}
EANMisconceptionsPanel.spec = {
  children: t.childrenArray(t.elementType(EANMisconceptionsDetail)),
};

export function EANMisconceptionsDetail(props: {|
  children:
    | [Element<typeof EANMisconceptionsItem>, Element<typeof EANPitfallItem>]
    | Element<typeof EANMisconceptionsItem>
    | Element<typeof EANPitfallItem>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    EANMisconceptionsDetail,
  );
  if (failedValidation) return null;
  const children = React.Children.toArray(props.children);
  return (
    <>
      {children.map((child, i) => (
        <React.Fragment key={i}>
          {child}
          {i !== children.length - 1 && <div style={{ height: 8 }} />}
        </React.Fragment>
      ))}
    </>
  );
}
EANMisconceptionsDetail.spec = {
  children: t.unionOfTypes(
    t.exactOrderChildrenArray(
      t.elementType(EANMisconceptionsItem),
      t.elementType(EANPitfallItem),
    ),
    t.elementType(EANMisconceptionsItem),
    t.elementType(EANPitfallItem),
  ),
};

// EANMisconceptionsItem and EANPitfallItem are used to express the same
// type of content, so they share types and specs.
type ItemChildren = ChildrenArray<
  | Element<typeof Text>
  | Element<typeof Latex>
  | Element<typeof LatexTextAtom>
  | Element<typeof List>
  | Element<typeof Image>
  | Element<typeof DraftImage>
  // probably only need those above this line
  | Element<typeof BoxAndWhiskerPlot>
  | Element<typeof CartesianPlane>
  | Element<typeof NumberLine>
  | Element<typeof StemAndLeafPlot>
  | Element<typeof TabularData>
  | Element<typeof TwoColumnProof>
  | Element<typeof AlgebraicWorking>,
>;
const itemChildrenSpec = t.childrenArray(
  t.unionOfTypes(
    // Primitives
    t.elementType(Text),
    t.elementType(Latex),
    t.elementType(LatexTextAtom),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    // probably only need those above this line
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(TabularData),
    t.elementType(TwoColumnProof),
    t.elementType(AlgebraicWorking),
  ),
);

export function EANMisconceptionsItem(props: {| children: ItemChildren |}) {
  const { failedValidation } = usePropsValidation(props, EANMisconceptionsItem);
  if (failedValidation) return null;
  return <>{props.children}</>;
}
EANMisconceptionsItem.spec = {
  children: itemChildrenSpec,
};

export function EANPitfallItem(props: {| children: ItemChildren |}) {
  const { failedValidation } = usePropsValidation(props, EANPitfallItem);
  if (failedValidation) return null;
  return (
    <div style={{ marginTop: 8, display: 'table', tableLayout: 'fixed' }}>
      {/* ideally this icon would be within the padding on the left, but 
      would require refactor and rewrite of how the panel works */}
      <div style={{ width: 16, display: 'table-cell' }}>
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.61065 9.38917C3.75854 11.5368 7.24104 11.5371 9.38917 9.38917C11.5371 7.24128 11.5368 3.75878 9.38917 1.61065C7.24128 -0.536764 3.75878 -0.537003 1.61065 1.61065C-0.537004 3.75878 -0.536765 7.24104 1.61065 9.38917ZM4.62376 2.60401C4.62376 2.11516 5.01997 1.71895 5.50882 1.71895C5.99766 1.71895 6.39387 2.11516 6.39387 2.60401L6.39387 6.14423C6.39387 6.63307 5.99766 7.02928 5.50882 7.02928C5.01997 7.02928 4.62376 6.63307 4.62376 6.14423L4.62376 2.60401ZM6.36359 8.46375C6.37421 8.01798 6.03435 7.65688 5.52456 7.65688C4.99353 7.65688 4.65396 8.01798 4.65367 8.46375C4.66458 8.93077 4.99382 9.28125 5.51423 9.28125C6.03435 9.28125 6.37421 8.93106 6.36359 8.46375Z"
            // this is not a product color but using this color was requested by design so
            fill={illustrationColor.tallPoppy}
          />
        </svg>
      </div>
      <div style={{ display: 'table-cell' }}>{props.children}</div>
    </div>
  );
}
EANPitfallItem.spec = {
  children: itemChildrenSpec,
};
